import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Save, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const NUMERIC_FIELDS = [
  ['distance_warn_km', 'Distância — warn (km)'],
  ['distance_block_km', 'Distância — block (km)'],
  ['time_drift_warn_hours', 'Tempo — warn (h)'],
  ['time_drift_block_hours', 'Tempo — block (h)'],
  ['ip_spam_warn_count', 'IP spam — warn'],
  ['ip_spam_block_count', 'IP spam — block'],
  ['pt_lat_min', 'Lat min (PT)'],
  ['pt_lat_max', 'Lat max (PT)'],
  ['pt_lng_min', 'Lng min (PT)'],
  ['pt_lng_max', 'Lng max (PT)'],
];

const SettingsPage = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery('moderation-settings', () => apiService.getModerationSettings());
  const [form, setForm] = useState(null);
  const [newReason, setNewReason] = useState('');

  useEffect(() => {
    if (data) setForm({ ...data });
  }, [data]);

  const mutation = useMutation((payload) => apiService.updateModerationSettings(payload), {
    onSuccess: (saved) => {
      toast.success('Configurações guardadas');
      qc.setQueryData('moderation-settings', saved);
      setForm({ ...saved });
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Falha ao guardar'),
  });

  if (isLoading || !form) return <LoadingSpinner />;

  const onChange = (k, v) => setForm({ ...form, [k]: v });

  const onSave = () => {
    const payload = { ...form };
    NUMERIC_FIELDS.forEach(([k]) => {
      payload[k] = Number(payload[k]);
    });
    mutation.mutate(payload);
  };

  const addReason = () => {
    const r = newReason.trim();
    if (!r) return;
    onChange('rejection_reasons', [...(form.rejection_reasons || []), r]);
    setNewReason('');
  };

  const removeReason = (idx) => {
    onChange('rejection_reasons', form.rejection_reasons.filter((_, i) => i !== idx));
  };

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configurações de Moderação</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Ajusta thresholds e razões de rejeição. Apenas admins.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {NUMERIC_FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
            <input
              type="number"
              step="any"
              value={form[k] ?? ''}
              onChange={(e) => onChange(k, e.target.value)}
              className="mt-1 w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </label>
        ))}
        <label className="block sm:col-span-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Versão mínima da app</span>
          <input
            value={form.min_app_version || ''}
            onChange={(e) => onChange('min_app_version', e.target.value)}
            placeholder="1.5.0"
            className="mt-1 w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </label>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Razões de rejeição</h3>
        <div className="space-y-1">
          {(form.rejection_reasons || []).map((r, i) => (
            <div key={`${r}-${i}`} className="flex items-center justify-between px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-700/50 text-sm">
              <span className="text-gray-800 dark:text-gray-200">{r}</span>
              <button onClick={() => removeReason(i)} className="text-gray-500 hover:text-fire-500">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <input
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addReason()}
            placeholder="Nova razão"
            className="flex-1 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <button
            onClick={addReason}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            <Plus className="w-4 h-4" /> Adicionar
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={mutation.isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {mutation.isLoading ? 'A guardar…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
